import React from "react";
import { connect, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import { useHistory, useLocation, useParams } from "react-router-dom";
import * as Yup from "yup";
import { TextInput, Checkbox } from "../../../commons/formik";
import { TOS, PRIVACY_POLICY, EVENTS } from "../../../constants";
import { ButtonContainer, Button } from "../../../commons/elements";

const propTypes = {
  clientId: PropTypes.number.isRequired,
  createAccount: PropTypes.func.isRequired,
  // Map state to props
  nextUrl: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired
};

function CreateAccountForm(props) {
  const { clientId, createAccount, nextUrl, isSubmitting } = props;
  const { tabName } = useSelector((state) => state.filters.filters);
  const history = useHistory();
  const location = useLocation();
  const params = useParams();

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    acceptedTerms: false
  };

  const CreateAccountSchema = Yup.object().shape({
    firstName: Yup.string().required("Please enter your first name"),
    lastName: Yup.string().required("Please enter your last name"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Please enter a valid email"),
    confirmEmail: Yup.mixed()
      .oneOf([Yup.ref("email"), null], "Emails don't match")
      .required("Please confirm your email"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters long")
      .matches(
        /(?:[A-Z].*[0-9])|(?:[0-9].*[A-Z])/g,
        "Include at least one upper case letter and a number"
      )
      .required("Please enter a password"),
    confirmPassword: Yup.mixed()
      .oneOf([Yup.ref("password"), null], "Passwords don't match")
      .required("Please confirm your password"),
    acceptedTerms: Yup.boolean()
      .required("This field is required")
      .oneOf([true], "You must accept the terms and conditions.")
  });

  const handleSubmit = (values) => {
    createAccount({
      ...values,
      clientId,
      history,
      location,
      nextUrl,
      shortName: params.shortName
    });
  };

  return (
    <div className="form-container">
      <Formik
        initialValues={initialValues}
        validationSchema={CreateAccountSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty }) => (
          <Form>
            <TextInput
              id="create-first-name"
              label="First Name"
              name="firstName"
              type="text"
              placeholder="First Name"
              labelProps={{ className: "lbl-first-name" }}
            />
            <TextInput
              id="create-last-name"
              label="Last Name"
              name="lastName"
              type="text"
              placeholder="Last Name"
              labelProps={{ className: "lbl-last-name" }}
            />
            <TextInput
              id="create-account-email"
              label="Email"
              name="email"
              type="email"
              placeholder="example@email.com"
              labelProps={{ className: "lbl-email" }}
            />
            <TextInput
              id="create-account-confirm-email"
              label="Confirm Email"
              name="confirmEmail"
              type="email"
              placeholder="example@email.com"
              labelProps={{ className: "lbl-confirm-email" }}
            />
            <TextInput
              id="create-account-password"
              label="Password"
              name="password"
              type="password"
              placeholder="password"
              labelProps={{ className: "lbl-password" }}
            />
            <TextInput
              id="create-account-confirm-password"
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              placeholder="password"
              labelProps={{ className: "lbl-confirm-password" }}
            />
            <Checkbox
              labelProps={{ className: "pt-2 terms-conditions small-lbl" }}
              name="acceptedTerms"
            >
              <span>
                I Accept{" "}
                <a
                  className="small-link"
                  href={TOS}
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  className="small-link"
                  href={PRIVACY_POLICY}
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              </span>
            </Checkbox>
            <ButtonContainer>
              <Button
                className="cancel-btn"
                label="Cancel"
                disabled={isSubmitting}
                onClick={() =>
                  history.push(`/${params.shortName}/${EVENTS}/${tabName}`)
                }
              />
              <Button
                type="submit"
                label="Register"
                disabled={!(isValid && dirty) || isSubmitting}
                isSubmitting={isSubmitting}
              />
            </ButtonContainer>
          </Form>
        )}
      </Formik>
    </div>
  );
}

CreateAccountForm.propTypes = propTypes;

const mapStateToProps = (state) => ({
  nextUrl: state.user.nextUrl,
  isSubmitting: state.user.isSubmitting
});

export default connect(mapStateToProps)(CreateAccountForm);
